const BASE = process.env.REACT_APP_API_URL || "";

async function req(method, path, body = null, token = null) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    const e = new Error("Can't reach the server right now. Try again in a minute.");
    e.network = true;
    throw e;
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    let detail = err.detail || "Request failed";
    if (detail === "SERVER_ERROR") detail = "The server hit an error. Try again in a minute, and contact support if it keeps happening.";
    const e = new Error(detail);
    if (detail === "SUBSCRIPTION_EXPIRED") e.subscriptionExpired = true;
    if (res.status === 401 && token) {
      e.sessionExpired = true;
      window.dispatchEvent(new Event("rbxwar:session-expired"));
    }
    throw e;
  }
  return res.json();
}

export const api = {
  login:        (key, code)     => req("POST", "/auth/login", { key, ...(code ? { code } : {}) }),
  activate:     (key, existingPhrase) => req("POST", "/auth/activate", { key, ...(existingPhrase ? { existing_recovery_phrase: existingPhrase } : {}) }),
  logout:       (token)         => req("POST", "/auth/logout", null, token),
  botStatus:    (token)         => req("GET",  "/bot/status", null, token),
  botStart:     (token)         => req("POST", "/bot/start", null, token),
  botStop:      (token)         => req("POST", "/bot/stop", null, token),
  botPause:     (token)         => req("POST", "/bot/pause", null, token),
  botResume:    (token)         => req("POST", "/bot/resume", null, token),
  getConfig:    (token)         => req("GET",  "/config", null, token),
  saveConfig:   (token, cfg)    => req("POST", "/config", cfg, token),
  addAccount:     (token, data)           => req("POST", "/config/add-account",    data,            token),
  removeAccount:  (token, index)          => req("POST", "/config/remove-account", { index },        token),
  update2fa:      (token, index, secret)  => req("POST", "/config/update-2fa",     { index, secret }, token),
  analytics:    (token)         => req("GET",  "/analytics", null, token),
  recentSales:  (token)         => req("GET",  "/sales/recent", null, token),
  accountLimits:(token)         => req("GET",  "/account/limits", null, token),
  listProxies:  (token)         => req("GET",  "/proxies", null, token),
  listPresets:  (token)         => req("GET",  "/presets", null, token),
  applyPreset:  (token, presetId, extra) => req("POST", "/presets/apply", { preset_id: presetId, ...extra }, token),
  removePreset: (token, presetId) => req("POST", "/presets/remove", { preset_id: presetId }, token),
  getDisplayName: (token) => req("GET", "/user/display-name", null, token),
  setDisplayName: (token, name) => req("POST", "/user/display-name", { display_name: name }, token),
  resetBackfill:(token)         => req("POST", "/sales/reset-backfill", {}, token),
  addProxy:     (token, proxy)  => req("POST", "/proxies", { proxy }, token),
  removeProxy:  (token, id)     => req("DELETE", `/proxies/${id}`, null, token),
  clearAccounts: (token)        => req("POST", "/config/clear-accounts", {}, token),
  clearProxies:  (token)        => req("POST", "/proxies/clear-all", {}, token),
  clearPresets:  (token)        => req("POST", "/presets/clear-all", {}, token),
  setSelectiveListing: (token, enabled) => req("POST", "/inventory/selective-mode", { enabled }, token),
  getSecurityStatus:  (token)           => req("GET",  "/user/security-status", null, token),
  loginPassword:      (username, password, code) => req("POST", "/auth/login-password", { username, password, code }),
  setPassword:        (token, password, code)    => req("POST", "/user/password", { password, code }, token),
  twoFaStart:         (token)                    => req("POST", "/user/2fa/start", {}, token),
  twoFaVerify:        (token, code)              => req("POST", "/user/2fa/verify", { code }, token),
  newBackupCodes:     (token, code)              => req("POST", "/user/2fa/backup-codes", { code }, token),
  supportAsk:         (token, question) => req("POST", "/support/ask", { question }, token),
  supportMine:        (token)           => req("GET",  "/support/mine", null, token),

  // Admin
  adminGenKey:  (secret, tier, note) => req("POST", "/admin/generate-key", { tier, note }, null, secret),
  adminKeys:    (secret)        => req("GET",  "/admin/keys", null, null, secret),
  adminRevoke:  (secret, key)   => req("POST", "/admin/revoke-key", { key }, null, secret),
  adminUsers:   (secret)        => req("GET",  "/admin/users", null, null, secret),
  adminStats:   (secret)        => req("GET",  "/admin/stats", null, null, secret),
};

// Admin helper with header
export async function adminReq(method, path, token, body = null) {
  const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };
  const res = await fetch(`${BASE}${path}`, {
    method, headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event("rbxwar:session-expired"));
    throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
  }
  return res.json();
}

export function connectWS(userId, token, onMessage, onStatusChange = () => {}) {
  const wsBase = (BASE || window.location.origin).replace("http", "ws");
  let ws = null;
  let closedByUs = false;
  let retryDelay = 1000;
  let retryTimer = null;

  const connect = () => {
    ws = new WebSocket(`${wsBase}/ws/${userId}?token=${token}`);

    ws.onopen = () => {
      retryDelay = 1000; // reset backoff on a good connection
      onStatusChange("open");
    };

    ws.onmessage = (e) => {
      try { onMessage(JSON.parse(e.data)); } catch {}
    };

    ws.onclose = () => {
      onStatusChange("closed");
      if (closedByUs) return;
      // Reconnect with capped exponential backoff — covers Safari/mobile
      // browsers killing background-tab sockets after a while.
      retryTimer = setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 15000);
        connect();
      }, retryDelay);
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  };

  connect();

  return {
    close: () => {
      closedByUs = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws?.close();
    },
    // Force an immediate reconnect attempt (e.g. when the tab regains focus)
    reconnectIfNeeded: () => {
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (retryTimer) clearTimeout(retryTimer);
        retryDelay = 1000;
        connect();
      }
    },
  };
}
